(() => {
  class CTV61Carousel extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.autoplayTimer = null;
      this.userPaused = false;
      this.isVisible = false;
      this.hasAutoplay = false;
      this.rafId = null;
      this.abortController = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-ct-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-ct-slide]'));
      if (!this.viewport || this.slides.length === 0) return;

      this.previousButton = this.querySelector('[data-ct-prev]');
      this.nextButton = this.querySelector('[data-ct-next]');
      this.dotButtons = Array.from(this.querySelectorAll('[data-ct-dot]'));
      this.pauseButton = this.querySelector('[data-ct-pause]');
      this.pauseIcon = this.querySelector('[data-ct-pause-icon]');
      this.pauseLabel = this.querySelector('[data-ct-pause-label]');
      this.status = this.querySelector('[data-ct-status]');
      this.progress = this.querySelector('[data-ct-progress]');
      this.count = this.querySelector('[data-ct-count]');
      this.autoplayDelay = Number.parseInt(this.dataset.autoplay || '0', 10);
      this.hasAutoplay = this.autoplayDelay > 0 && this.slides.length > 1;
      this.itemLabel = this.getItemLabel();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.viewport.addEventListener('scroll', () => this.handleScroll(), { passive: true, signal });
      this.viewport.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });

      this.previousButton?.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(this.activeIndex - 1);
      }, { signal });

      this.nextButton?.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(this.activeIndex + 1);
      }, { signal });

      this.dotButtons.forEach((button, index) => {
        button.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(index);
        }, { signal });
      });

      if (this.hasAutoplay) {
        this.viewport.addEventListener('pointerdown', () => this.stopAutoplay(), { passive: true, signal });
        this.pauseButton?.addEventListener('click', () => this.toggleAutoplay(), { signal });

        this.addEventListener('mouseenter', () => this.clearAutoplay(), { signal });
        this.addEventListener('mouseleave', () => this.startAutoplay(), { signal });
        this.addEventListener('focusin', () => this.clearAutoplay(), { signal });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.startAutoplay();
        }, { signal });
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.clearAutoplay();
          else this.startAutoplay();
        }, { signal });

        this.reducedMotion.addEventListener?.('change', () => {
          this.updatePauseControl();
          this.startAutoplay();
        }, { signal });

        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.isVisible = entry?.isIntersecting ?? false;
            if (this.isVisible) this.startAutoplay();
            else this.clearAutoplay();
          },
          { threshold: 0.2 }
        );
        this.intersectionObserver.observe(this);
      }

      this.resizeObserver = new ResizeObserver(() => this.handleScroll());
      this.resizeObserver.observe(this.viewport);

      this.updateUi();
      if (this.hasAutoplay) {
        this.updatePauseControl();
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.clearAutoplay();
      if (this.rafId) window.cancelAnimationFrame(this.rafId);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.abortController?.abort();
    }

    getItemLabel() {
      if (this.classList.contains('ct61-carousel--products')) return 'Producto';
      if (this.closest('.ct61-reviews')) return 'Reseña';
      return 'Historia';
    }

    handleScroll() {
      if (this.rafId) return;
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.updateActiveIndex();
      });
    }

    handleKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.stopAutoplay();
      this.goTo(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }

    updateActiveIndex() {
      const viewportRect = this.viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.activeIndex) {
        this.activeIndex = closestIndex;
        this.updateUi();
      }
    }

    goTo(index, options = {}) {
      const lastIndex = this.slides.length - 1;
      const targetIndex = options.loop
        ? (index + this.slides.length) % this.slides.length
        : Math.max(0, Math.min(index, lastIndex));
      const target = this.slides[targetIndex];
      if (!target) return;
      const viewportRect = this.viewport.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetLeft = this.viewport.scrollLeft + targetRect.left - viewportRect.left;

      this.activeIndex = targetIndex;
      const previousScrollBehavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollTo({
        left: targetLeft,
        behavior: 'auto'
      });
      this.viewport.style.scrollBehavior = previousScrollBehavior;
      this.updateUi();
    }

    updateUi() {
      const total = this.slides.length;
      const current = this.activeIndex + 1;

      this.slides.forEach((slide, index) => {
        slide.toggleAttribute('data-ct-active', index === this.activeIndex);
      });

      this.dotButtons.forEach((button, index) => {
        const isActive = index === this.activeIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex === total - 1;
      if (this.status) this.status.textContent = `${this.itemLabel} ${current} de ${total}`;
      if (this.count) {
        this.count.textContent = `${String(current).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      }
      if (this.progress) {
        this.progress.style.setProperty('--ct-progress', `${(current / total) * 100}%`);
      }
    }

    toggleAutoplay() {
      if (!this.hasAutoplay || this.reducedMotion.matches) return;
      this.userPaused = !this.userPaused;
      if (this.status) this.status.setAttribute('aria-live', this.userPaused ? 'polite' : 'off');
      this.updatePauseControl();
      if (this.userPaused) this.clearAutoplay();
      else this.startAutoplay();
    }

    stopAutoplay() {
      if (!this.hasAutoplay) return;
      if (this.status) this.status.setAttribute('aria-live', 'polite');
      this.userPaused = true;
      this.updatePauseControl();
      this.clearAutoplay();
    }

    startAutoplay() {
      this.clearAutoplay();
      if (
        !this.hasAutoplay ||
        this.userPaused ||
        this.reducedMotion.matches ||
        !this.isVisible ||
        document.hidden ||
        this.slides.length < 2
      ) return;

      if (this.status) this.status.setAttribute('aria-live', 'off');
      this.autoplayTimer = window.setInterval(() => {
        this.goTo(this.activeIndex + 1, { loop: true });
      }, this.autoplayDelay);
    }

    clearAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    updatePauseControl() {
      if (!this.pauseButton || !this.hasAutoplay) return;

      if (this.reducedMotion.matches) {
        this.pauseButton.disabled = true;
        this.pauseButton.setAttribute('aria-label', 'Sin animación');
        if (this.pauseIcon) this.pauseIcon.textContent = '—';
        if (this.pauseLabel) this.pauseLabel.textContent = 'Sin animación';
        return;
      }

      this.pauseButton.disabled = false;
      const isPaused = this.userPaused;
      this.pauseButton.setAttribute('aria-label', isPaused ? 'Reanudar' : 'Pausar');
      this.pauseButton.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
      if (this.pauseIcon) this.pauseIcon.textContent = isPaused ? '▶' : 'Ⅱ';
      if (this.pauseLabel) this.pauseLabel.textContent = isPaused ? 'Reanudar' : 'Pausar';
    }
  }

  if (!customElements.get('ct-v61-carousel')) {
    customElements.define('ct-v61-carousel', CTV61Carousel);
  }
})();


